import React, { useMemo, useState } from "react";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import Skeleton from "@mui/material/Skeleton";

import Box from "components/common/Box";
import Stack from "components/common/Stack";
import Text from "components/common/Text";
import Button from "components/common/Button";
import { formatHelper } from "helper/format";

const FREQUENCY_LABEL = {
  HALF_YEARLY: "Semi-Annually",
  YEARLY: "Annually",
  QUARTERLY: "Quarterly",
  MONTHLY: "Monthly",
};

const TOGGLE_LABEL = {
  HALF_YEARLY: "Half-Yearly",
  YEARLY: "Yearly",
};

const BENEFITS = [
  "AI Personalized Portfolio Management",
  "Adaptive Intelligence for Smarter, High–Conviction Opportunities",
  "Live Daily Performance Reports with Deep Portfolio Intelligence",
  "Jarvis Protect+ for Real–Time Risk Protection",
  "Tax Optimized Rebalancing with Real–Time Risk–Reward Alignment",
  "Dedicated Relationship Manager & Priority WhatsApp Support",
];

const FALLBACK_PLANS = [
  { planFrequency: "HALF_YEARLY", planPrice: 1.25, planCode: "JPR001" },
  { planFrequency: "YEARLY", planPrice: 2.5, planCode: "JPR002" },
];

const CARD_GRADIENT = "linear-gradient(162deg, #6A5014 2%, #E4B13B 98%)";

const PeriodToggle = ({ value, onChange }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "stretch",
      width: 172.5,
      height: 39.5,
      p: "3.75px",
      borderRadius: 999,
      bgcolor: "#F3F4F6",
      border: "1px solid #E5E7EB",
      boxShadow:
        "0 9px 22px -9px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)",
      overflow: "hidden",
    }}
  >
    {["HALF_YEARLY", "YEARLY"].map((period) => {
      const active = value === period;
      return (
        <Box
          key={period}
          component="button"
          type="button"
          onClick={() => onChange(period)}
          sx={{
            flex: 1,
            border: 0,
            cursor: "pointer",
            borderRadius: 999,
            bgcolor: active ? "#fff" : "transparent",
            boxShadow: active ? "0 2px 4px rgba(0,0,0,0.05)" : "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: 0.5,
          }}
        >
          <Text
            sx={{
              fontSize: 13,
              fontWeight: active ? 600 : 500,
              color: active ? "#111827" : "#6B7280",
              lineHeight: 1,
              whiteSpace: "nowrap",
            }}
          >
            {TOGGLE_LABEL[period]}
          </Text>
        </Box>
      );
    })}
  </Box>
);

const PrimePricingCardSkeleton = () => (
  <Box
    sx={{
      flex: 1,
      width: "100%",
      minWidth: 0,
      p: 2.5,
      borderRadius: "20px",
      bgcolor: "#fff",
      border: "1px solid #E2E8F0",
      boxShadow: "0 4px 6px rgba(0,0,0,0.04)",
    }}
  >
    <Stack
      spacing={4}
      sx={{
        p: 2.5,
        height: "100%",
        minHeight: 420,
        borderRadius: "16px",
        bgcolor: "#F1F5F9",
      }}
    >
      <Stack spacing={2}>
        <Stack direction="row" spacing={1.5}>
          <Skeleton variant="rounded" width={110} height={28} sx={{ borderRadius: "14px" }} />
          <Skeleton variant="rounded" width={90} height={28} sx={{ borderRadius: "14px" }} />
        </Stack>
        <Stack spacing={1}>
          <Skeleton variant="rounded" width="60%" height={32} />
          <Skeleton variant="text" width={72} height={16} />
        </Stack>
        <Skeleton variant="rounded" width="100%" height={42} sx={{ borderRadius: "14px" }} />
      </Stack>
      <Stack spacing={1.25}>
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <Stack key={i} direction="row" spacing={1.25} alignItems="center">
            <Skeleton variant="circular" width={24} height={24} />
            <Skeleton variant="text" width={`${75 - i * 6}%`} height={18} />
          </Stack>
        ))}
      </Stack>
    </Stack>
  </Box>
);

const PrimePricingSkeleton = () => (
  <>
    <Box
      sx={{
        display: { xs: "none", md: "flex" },
        justifyContent: "center",
        gap: 4,
        width: "100%",
      }}
    >
      <Box sx={{ flex: "1 1 0", minWidth: 0 }}>
        <PrimePricingCardSkeleton />
      </Box>
      <Box sx={{ flex: "1 1 0", minWidth: 0 }}>
        <PrimePricingCardSkeleton />
      </Box>
    </Box>

    <Stack spacing={2} sx={{ display: { xs: "flex", md: "none" }, width: "100%" }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ width: "100%" }}
      >
        <Text sx={{ fontSize: 18, fontWeight: 700, color: "#0F172A", lineHeight: "24px" }}>
          Pricing
        </Text>
        <Skeleton variant="rounded" width={172.5} height={39.5} sx={{ borderRadius: 999 }} />
      </Stack>
      <PrimePricingCardSkeleton />
    </Stack>
  </>
);

const PrimePricingCard = ({ plan, onUnlock, showFrequencyBadge = true }) => {
  const frequencyLabel =
    FREQUENCY_LABEL[plan.planFrequency] || plan.planFrequency || "";
  const percent = formatHelper.parseDecimal(plan.planPrice);
  const useFigmaCheck = !showFrequencyBadge;

  return (
    <Box
      sx={{
        flex: 1,
        width: "100%",
        minWidth: 0,
        p: 2.5,
        borderRadius: "20px",
        bgcolor: "#fff",
        border: "1px solid #E2E8F0",
        boxShadow: "0 4px 6px rgba(0,0,0,0.04)",
      }}
    >
      <Stack
        spacing={4}
        sx={{
          p: 2.5,
          height: "100%",
          borderRadius: "16px",
          background: CARD_GRADIENT,
          boxShadow: "0 10px 12px rgba(29,78,216,0.13)",
        }}
      >
        <Stack spacing={2}>
          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
            <Box
              sx={{
                px: 2,
                py: 0.5,
                borderRadius: "14px",
                bgcolor: "#fff",
              }}
            >
              <Text sx={{ fontSize: 12, fontWeight: 500, color: "#735717" }}>
                HNIs/ UHNIs
              </Text>
            </Box>
            {showFrequencyBadge ? (
              <Box
                sx={{
                  px: 2,
                  py: 0.5,
                  borderRadius: "14px",
                  bgcolor: "#fff",
                }}
              >
                <Text sx={{ fontSize: 12, fontWeight: 500, color: "#735717" }}>
                  {frequencyLabel}
                </Text>
              </Box>
            ) : null}
          </Stack>

          <Stack spacing={1}>
            <Text sx={{ color: "#fff", lineHeight: 1.3 }}>
              <Box component="span" sx={{ fontSize: 28, fontWeight: 700 }}>
                {percent}%
              </Box>{" "}
              <Box component="span" sx={{ fontSize: 18, fontWeight: 600 }}>
                of AUM
              </Box>
              <Box component="span" sx={{ fontSize: 18, fontWeight: 400 }}>
                {` / ${frequencyLabel}`}
              </Box>
            </Text>
            <Text sx={{ fontSize: 12, color: "rgba(255,255,255,0.9)" }}>
              Excl. GST
            </Text>
          </Stack>

          <Button
            onClick={() => onUnlock?.(plan)}
            endIcon={<ArrowForwardRoundedIcon sx={{ fontSize: 20 }} />}
            sx={{
              width: "100%",
              height: 42,
              borderRadius: "14px",
              textTransform: "none",
              fontWeight: 600,
              fontSize: 16,
              color: "#E4B13B",
              bgcolor: "#fff",
              boxShadow: "0 10px 12px rgba(228,177,59,0.2)",
              "&:hover": { bgcolor: "#fff" },
            }}
          >
            Unlock
          </Button>
        </Stack>

        <Stack spacing={1.25}>
          {BENEFITS.map((benefit) => (
            <Stack
              key={benefit}
              direction="row"
              spacing={1.25}
              alignItems="flex-start"
            >
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  flexShrink: 0,
                  borderRadius: useFigmaCheck ? "12px" : "50%",
                  bgcolor: "#fff",
                  display: "grid",
                  placeItems: "center",
                  mt: "1px",
                  overflow: "hidden",
                }}
              >
                {useFigmaCheck ? (
                  <Box
                    component="img"
                    src="/images/prime-product-info/pricing-check.svg"
                    alt=""
                    sx={{ width: 24, height: 24, display: "block" }}
                  />
                ) : (
                  <CheckRoundedIcon sx={{ fontSize: 16, color: "#E4B13B" }} />
                )}
              </Box>
              <Text
                sx={{
                  fontSize: 14,
                  color: "#fff",
                  lineHeight: "21px",
                }}
              >
                {benefit}
              </Text>
            </Stack>
          ))}
        </Stack>
      </Stack>
    </Box>
  );
};

/**
 * Prime product-info pricing (Figma). Separate from shared JarvisSubscriptionPlans.
 */
const PrimePricingSection = ({ plans = [], loading = false, onUnlock }) => {
  const [period, setPeriod] = useState("YEARLY");

  const displayPlans = useMemo(() => {
    if (loading) return [];

    const fromApi = (plans || []).filter(
      (p) =>
        p.planFrequency === "HALF_YEARLY" || p.planFrequency === "YEARLY",
    );

    if (fromApi.length) {
      return [...fromApi]
        .map((p) => {
          const fallback =
            FALLBACK_PLANS.find((f) => f.planFrequency === p.planFrequency) ||
            FALLBACK_PLANS[0];
          return {
            ...fallback,
            ...p,
            planPrice: Number(p.planPrice ?? fallback.planPrice) || fallback.planPrice,
          };
        })
        .sort((a, b) => {
          const order = { HALF_YEARLY: 0, YEARLY: 1 };
          return (order[a.planFrequency] ?? 9) - (order[b.planFrequency] ?? 9);
        });
    }

    return FALLBACK_PLANS;
  }, [plans, loading]);

  const selectedPlan =
    displayPlans.find((p) => p.planFrequency === period) || displayPlans[0];
  const isSinglePlan = displayPlans.length === 1;

  if (loading) {
    return <PrimePricingSkeleton />;
  }

  return (
    <>
      {/* Desktop — center when only one plan */}
      <Box
        sx={{
          display: { xs: "none", md: "flex" },
          justifyContent: "center",
          gap: 4,
          width: "100%",
        }}
      >
        {displayPlans.map((plan) => (
          <Box
            key={plan.planCode || plan.planFrequency}
            sx={{
              flex: isSinglePlan ? "0 1 640px" : "1 1 0",
              width: isSinglePlan ? "100%" : "auto",
              maxWidth: isSinglePlan ? 640 : "none",
              minWidth: 0,
            }}
          >
            <PrimePricingCard plan={plan} onUnlock={onUnlock} />
          </Box>
        ))}
      </Box>

      {/* Mobile — Pricing + Half-Yearly / Yearly toggle (hide toggle if 1 plan) */}
      <Stack
        spacing={2}
        sx={{ display: { xs: "flex", md: "none" }, width: "100%" }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ width: "100%" }}
        >
          <Text sx={{ fontSize: 18, fontWeight: 700, color: "#0F172A", lineHeight: "24px" }}>
            Pricing
          </Text>
          {!isSinglePlan ? (
            <PeriodToggle value={period} onChange={setPeriod} />
          ) : null}
        </Stack>
        {selectedPlan ? (
          <PrimePricingCard
            plan={selectedPlan}
            onUnlock={onUnlock}
            showFrequencyBadge={false}
          />
        ) : null}
      </Stack>
    </>
  );
};

export default PrimePricingSection;
